/**
 * DAG Utilities for Consensus Event Processing
 * Handles reconstruction of DAG from flattened consensus events,
 * orphan resolution, and data transformation for timeline/graph views
 */

import type {
  ConsensusEventFlat,
  ConsensusItemFlat,
  ConsensusVertexItemFlat,
  ConsensusTxItemFlat,
  ConsensusUnconfirmedTxItemFlat,
} from '../sdk/types/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface DAGNode {
  id: string;
  type: 'vertex' | 'tx' | 'unconfirmed_tx';
  timestamp: number;
  parents: string[]; // For vertices: parent_ids; for txs: inferred from containing vertex
  children: string[]; // Computed via reverse lookup
  data: ConsensusItemFlat;
  vertexId?: string; // For txs: link to containing vertex (if inferred)
  shardId: number;
}

export interface TxItem {
  id: string;
  timestamp: number;
  parents: string[];
  children: string[];
  data: ConsensusItemFlat;
  nodeType: 'vertex' | 'tx' | 'unconfirmed_tx';
  shardId: number;
}

export interface TimelineItem {
  id: string;
  content: string;
  start: Date;
  group?: string; // For branching (lineage-based grouping)
  title?: string; // Tooltip with details
  className?: string; // For styling
}

export interface GraphData {
  nodes: { id: string; label: string; type: string; [key: string]: any }[];
  links: { source: string; target: string }[];
}

export interface DagState {
  vertexMap: Map<string, DAGNode>;
  txMap: Map<string, DAGNode>;
  parentToChildren: Map<string, Set<string>>;
  orphanMap: Map<string, ConsensusVertexItemFlat>;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isVertexItem(data: ConsensusItemFlat): data is ConsensusVertexItemFlat {
  return data.type === 'vertex';
}

export function isTxItem(data: ConsensusItemFlat): data is ConsensusTxItemFlat {
  return data.type === 'tx';
}

export function isUnconfirmedTxItem(
  data: ConsensusItemFlat,
): data is ConsensusUnconfirmedTxItemFlat {
  return data.type === 'unconfirmed_tx';
}

// ============================================================================
// DAG STATE MANAGEMENT
// ============================================================================

export function createDagState(): DagState {
  return {
    vertexMap: new Map(),
    txMap: new Map(),
    parentToChildren: new Map(),
    orphanMap: new Map(),
  };
}

export function clearDagState(state: DagState): void {
  state.vertexMap.clear();
  state.txMap.clear();
  state.parentToChildren.clear();
  state.orphanMap.clear();
}

// ============================================================================
// ORPHAN RESOLUTION
// ============================================================================

const ORPHAN_RESOLVE_TIMEOUT = 30000; // 30 seconds
const MAX_ORPHANS = 1000;

/**
 * Resolve orphaned vertices (vertices with missing parents)
 */
export function resolveOrphans(
  newVertex: ConsensusVertexItemFlat,
  state: DagState,
): void {
  // Check if any existing orphans have this vertex as a parent
  for (const [orphanId, orphan] of state.orphanMap.entries()) {
    if (orphan.parent_ids?.includes(newVertex.id)) {
      // Resolve: add to parent->children mapping
      if (!state.parentToChildren.has(newVertex.id)) {
        state.parentToChildren.set(newVertex.id, new Set());
      }
      state.parentToChildren.get(newVertex.id)!.add(orphanId);
      state.orphanMap.delete(orphanId);
    }
  }

  // Check if new vertex itself is an orphan
  if (newVertex.parent_ids && newVertex.parent_ids.length > 0) {
    const allParentsExist = newVertex.parent_ids.every((parentId) =>
      state.vertexMap.has(parentId),
    );

    if (!allParentsExist) {
      // New vertex is orphaned
      if (state.orphanMap.size >= MAX_ORPHANS) {
        // Evict oldest orphan (FIFO)
        const firstKey = state.orphanMap.keys().next().value;
        if (firstKey) {
          state.orphanMap.delete(firstKey);
          console.warn(`[dagUtils] Orphan buffer full, evicted: ${firstKey}`);
        }
      }
      state.orphanMap.set(newVertex.id, newVertex);
      console.warn(`[dagUtils] Orphaned vertex detected: ${newVertex.id}`);
    }
  }
}

/**
 * Clean up orphaned vertices that couldn't be resolved (timeout)
 */
export function cleanupOrphans(state: DagState, _timeoutMs = ORPHAN_RESOLVE_TIMEOUT): void {
  // This should be called periodically or on timeout
  for (const [orphanId] of state.orphanMap.entries()) {
    // Mark as permanently missing (could add timestamp tracking for better logic)
    console.warn(`[dagUtils] Orphan timeout: ${orphanId} - marking as missing parent`);
    state.orphanMap.delete(orphanId);
  }
}

// ============================================================================
// TX TO VERTEX MATCHING
// ============================================================================

const TX_VERTEX_MATCH_WINDOW_MS = 30000; // 30 seconds

/**
 * Match a transaction to its containing vertex using timestamp + shard_id heuristic
 */
function matchTxToVertex(
  tx: ConsensusTxItemFlat | ConsensusUnconfirmedTxItemFlat,
  txTimestamp: number,
  state: DagState,
): string | null {
  const windowMs = TX_VERTEX_MATCH_WINDOW_MS;
  let bestMatch: { vertexId: string; timeDiff: number } | null = null;

  for (const [vertexId, vertex] of state.vertexMap.entries()) {
    // Match by: same shard_id + closest timestamp within window
    if (vertex.shardId !== tx.shard_id) continue;

    const timeDiff = Math.abs(vertex.timestamp - txTimestamp);
    if (timeDiff < windowMs) {
      if (!bestMatch || timeDiff < bestMatch.timeDiff) {
        bestMatch = { vertexId, timeDiff };
      }
    }
  }

  return bestMatch?.vertexId || null;
}

// ============================================================================
// DAG RECONSTRUCTION
// ============================================================================

/**
 * Reconstruct DAG node from consensus event
 */
export function reconstructDag(event: ConsensusEventFlat, state: DagState): DAGNode | null {
  const { data, event_time } = event;

  if (isVertexItem(data)) {
    const parents = data.parent_ids || [];

    const node: DAGNode = {
      id: data.id,
      type: 'vertex',
      timestamp: event_time || data.timestamp,
      parents,
      children: [],
      data: data,
      shardId: data.shard_id,
    };

    state.vertexMap.set(data.id, node);

    // Update parent->children mapping
    parents.forEach((parentId: string) => {
      if (!state.parentToChildren.has(parentId)) {
        state.parentToChildren.set(parentId, new Set());
      }
      state.parentToChildren.get(parentId)!.add(data.id);
    });

    // Update children for this node
    const childrenSet = state.parentToChildren.get(data.id);
    if (childrenSet) {
      node.children = Array.from(childrenSet);
    }

    // Resolve orphans
    resolveOrphans(data, state);

    return node;
  } else if (isTxItem(data) || isUnconfirmedTxItem(data)) {
    const node: DAGNode = {
      id: data.id || data.txhash || `tx-${Date.now()}`,
      type: isTxItem(data) ? 'tx' : 'unconfirmed_tx',
      timestamp: event_time || data.timestamp,
      parents: [], // Will be inferred from containing vertex
      children: [],
      data: data,
      shardId: data.shard_id,
    };

    state.txMap.set(node.id, node);

    // Infer containing vertex by finding nearest vertex in time
    const nearestVertexId = matchTxToVertex(data, node.timestamp, state);
    if (nearestVertexId) {
      const vertex = state.vertexMap.get(nearestVertexId);
      if (vertex) {
        node.vertexId = nearestVertexId;
        node.parents = vertex.parents; // Inherit vertex parents
      }
    }

    return node;
  }

  return null; // Skip validators for now
}

/**
 * Update children for all nodes (reverse lookup)
 */
export function updateChildrenForAll(state: DagState): void {
  // Recompute children for all nodes
  for (const node of [...state.vertexMap.values(), ...state.txMap.values()]) {
    const childrenSet = state.parentToChildren.get(node.id);
    node.children = childrenSet ? Array.from(childrenSet) : [];
  }
}

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

/**
 * Process a single consensus event message into TxItem
 */
export function processDagMsg(msg: ConsensusEventFlat, state: DagState): TxItem | null {
  const node = reconstructDag(msg, state);
  if (!node) return null;

  // Update children for all nodes
  updateChildrenForAll(state);

  return {
    id: node.id,
    timestamp: node.timestamp,
    parents: node.parents,
    children: node.children,
    data: node.data,
    nodeType: node.type,
    shardId: node.shardId,
  };
}

// ============================================================================
// MESSAGE BATCHER
// ============================================================================

/**
 * Batcher that accumulates messages and processes them in batches
 * This accumulates ConsensusEventFlat messages over a time window
 * and processes them together to avoid excessive updates
 */
class MessageBatcher {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private accumulatedMessages: ConsensusEventFlat[] = [];
  private readonly waitMs: number;

  constructor(waitMs: number = 500) {
    this.waitMs = waitMs;
  }

  /**
   * Add messages to the batch
   * @param newMsgs - New messages to add
   * @param state - Current DAG state
   * @param updateFn - Callback to process accumulated messages
   */
  add(
    newMsgs: ConsensusEventFlat[],
    state: DagState,
    updateFn: (processed: TxItem[]) => void,
  ): void {
    // Accumulate messages
    this.accumulatedMessages.push(...newMsgs);

    // Clear existing timeout
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
    }

    // Set new timeout
    this.timeoutId = setTimeout(() => {
      // Process all accumulated messages
      const processed = this.accumulatedMessages
        .map((msg) => processDagMsg(msg, state))
        .filter((item): item is TxItem => item !== null);

      // Clear accumulated messages
      this.accumulatedMessages = [];

      // Call update function with processed items
      updateFn(processed);

      this.timeoutId = null;
    }, this.waitMs);
  }

  /**
   * Cancel pending batch processing
   */
  cancel(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.accumulatedMessages = [];
  }
}

// Create a singleton batcher instance
const messageBatcher = new MessageBatcher(500);

/**
 * Debounced batcher for WebSocket updates
 * Accumulates messages over 500ms and processes them in a single batch
 */
export function batchUpdates(
  newMsgs: ConsensusEventFlat[],
  state: DagState,
  updateFn: (processed: TxItem[]) => void,
): void {
  messageBatcher.add(newMsgs, state, updateFn);
}

// ============================================================================
// TIMELINE DATA BUILDER
// ============================================================================

/**
 * Build timeline data with lineage-based grouping for branching visualization
 */
export function buildTimelineData(txs: TxItem[]): TimelineItem[] {
  // Group by lineage (parent chain) for hierarchical visualization
  const lineageMap = new Map<string, string>();

  const computeLineage = (tx: TxItem): string => {
    if (lineageMap.has(tx.id)) return lineageMap.get(tx.id)!;

    if (tx.parents.length === 0) {
      lineageMap.set(tx.id, 'root');
      return 'root';
    }

    // Build lineage from first parent (or combine if multiple)
    const parentLineage = tx.parents
      .map((p) => {
        const parentTx = txs.find((t) => t.id === p);
        if (parentTx) {
          return lineageMap.get(p) || computeLineage(parentTx);
        }
        return null;
      })
      .filter((l): l is string => l !== null)
      .join('-');

    const lineage = parentLineage || 'root';
    lineageMap.set(tx.id, lineage);
    return lineage;
  };

  return txs.map((tx) => {
    const lineage = computeLineage(tx);
    const typePrefix = tx.nodeType === 'vertex' ? 'V' : tx.nodeType === 'tx' ? 'TX' : 'UTX';
    const shortId = tx.id.length > 8 ? tx.id.slice(0, 8) + '...' : tx.id;

    return {
      id: tx.id,
      content: `${typePrefix} ${shortId}`,
      start: new Date(tx.timestamp * 1000), // Convert Unix timestamp to Date
      group: lineage, // Use lineage for grouping
      title: `${tx.nodeType} | Parents: ${tx.parents.length} | Children: ${tx.children.length} | Shard: ${tx.shardId}`,
      className: `dag-node-${tx.nodeType}`,
    };
  });
}

// ============================================================================
// GRAPH DATA BUILDER
// ============================================================================

/**
 * Build graph data from DAG relations with optional subgraph extraction
 */
export function buildGraphData(
  txs: TxItem[],
  rootId?: string,
  maxDepth = 5,
): GraphData {
  if (!rootId) {
    // For large datasets, require selection
    if (txs.length > 100) {
      return { nodes: [], links: [] };
    }
    // Small dataset: show all
    return {
      nodes: txs.map((tx) => ({
        id: tx.id,
        label: `${tx.nodeType === 'vertex' ? 'V' : 'TX'} ${tx.id.slice(0, 8)}`,
        type: tx.nodeType,
        shardId: tx.shardId,
      })),
      links: txs.flatMap((tx) =>
        tx.children.map((child) => ({ source: tx.id, target: child })),
      ),
    };
  }

  // BFS for descendants + ancestors
  const visited = new Set<string>();
  const queue: { id: string; depth: number }[] = [{ id: rootId, depth: 0 }];
  const nodes: any[] = [];
  const links: any[] = [];
  const nodeSet = new Set<string>();

  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (visited.has(id) || depth > maxDepth) continue;
    visited.add(id);

    const tx = txs.find((t) => t.id === id);
    if (!tx) continue;

    if (!nodeSet.has(id)) {
      const typePrefix = tx.nodeType === 'vertex' ? 'V' : 'TX';
      const shortId = tx.id.length > 8 ? tx.id.slice(0, 8) + '...' : tx.id;
      nodes.push({
        id: tx.id,
        label: `${typePrefix} ${shortId}`,
        type: tx.nodeType,
        shardId: tx.shardId,
      });
      nodeSet.add(id);
    }

    // Add children (descendants)
    tx.children.forEach((childId) => {
      links.push({ source: id, target: childId });
      if (!visited.has(childId)) {
        queue.push({ id: childId, depth: depth + 1 });
      }
    });

    // Add parents (ancestors) - reverse direction
    tx.parents.forEach((parentId) => {
      links.push({ source: parentId, target: id });
      if (!nodeSet.has(parentId)) {
        const parentTx = txs.find((t) => t.id === parentId);
        if (parentTx) {
          const typePrefix = parentTx.nodeType === 'vertex' ? 'V' : 'TX';
          const shortId =
            parentTx.id.length > 8 ? parentTx.id.slice(0, 8) + '...' : parentTx.id;
          nodes.push({
            id: parentTx.id,
            label: `${typePrefix} ${shortId}`,
            type: parentTx.nodeType,
            shardId: parentTx.shardId,
          });
          nodeSet.add(parentId);
        }
      }
      if (!visited.has(parentId)) {
        queue.push({ id: parentId, depth: depth + 1 });
      }
    });
  }

  return { nodes, links };
}

/**
 * Get all nodes for graph/timeline
 */
export function getAllNodes(state: DagState): DAGNode[] {
  return [...state.vertexMap.values(), ...state.txMap.values()];
}

